import {
  parametriFiltra,
  type FilterCelice,
  type KnjizbaZaPrikaz,
  type VrsticaPorocila,
} from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';

import { api } from '../lib/api';
import { znesekZValuto } from '../lib/format';
import { useBesedila, useJezik } from '../lib/jezik';
import { useUporabnik } from '../lib/uporabnik';
import { IzvirnaVrstica } from './IzvirnaVrstica';
import {
  preberiSirine,
  shraniSirine,
  TabelaKnjizb,
  type SirineStolpcev,
} from './TabelaKnjizb';

/**
 * En del razčlenitve: svoj filter in svoj seznam knjižb.
 *
 * Običajna celica ima en del. Pri primerjavi T/T-1 nosi stolpec razlike
 * filtra obeh let, zato ima dva — knjižbe posameznega leta so ločene, sicer
 * ne bi bilo videti, kaj se je med letoma spremenilo (docs/ARHITEKTURA.md, 6.2).
 */
export interface DelCelice {
  /** Napis nad delom, npr. leto pri primerjavi. */
  naslov: string;
  filter: FilterCelice;
  /** Znesek tega dela, kot je prikazan v poročilu. */
  znesek: string;
}

export interface Celica {
  vrstica: VrsticaPorocila;
  /**
   * Obdobja, ki jih celica zajema: en mesec, kumulativa do meseca ali — pri
   * stolpcu „skupaj" — vsa obdobja poročila.
   */
  obdobjeOd: number;
  obdobjeDo: number;
  /** Napis nad razčlenitvijo, npr. „januar" ali „januar – julij". */
  naslovObdobja: string;
  /** Znesek, kot je prikazan v celici. */
  znesek: string;
  /**
   * Deli razčlenitve, kadar ima celica svoje filtre (primerjava T/T-1).
   *
   * Brez tega velja filter vrstice z obdobji celice — tako kot pri poročilih,
   * kjer so stolpci meseci istega leta.
   */
  deli?: DelCelice[];
  /**
   * Nazivi nadrejenih vrstic, npr. [„Prihodki“] za „Čisti prihodki“. Glava
   * panela jih pokaže, da je vedno jasno, katera številka je odprta – panel
   * prekrije desni del poročila, kjer je bila kliknjena celica.
   */
  pot?: string[];
}

interface Lastnosti {
  podjetje: string;
  celica: Celica;
  naZapri: () => void;
}

/**
 * Parametri celice za zahtevo.
 *
 * Isti filter uporabita prikaz in izvoz v Excel - sicer bi se lahko razsla
 * (docs/ARHITEKTURA.md, 6.2).
 */
function parametriCelice(celica: Celica): [string, string][] {
  // Vec izbranih vrednosti dimenzije gre kot ponovljen parameter, zato pari in
  // ne slovar: slovar bi obdrzal samo zadnjo vrednost.
  return [
    ...parametriFiltra(celica.vrstica.filter).filter(
      ([k]) => k !== 'obdobjeOd' && k !== 'obdobjeDo',
    ),
    ['obdobjeOd', String(celica.obdobjeOd)],
    ['obdobjeDo', String(celica.obdobjeDo)],
  ];
}

/** Deli razčlenitve; brez lastnih filtrov je to en sam del iz vrstice. */
function deliCelice(celica: Celica): DelCelice[] {
  return celica.deli ?? [];
}

/** Parametri enega dela; obdobja so že v njegovem filtru. */
function parametriDela(celica: Celica, del: DelCelice | null): [string, string][] {
  return del === null ? parametriCelice(celica) : parametriFiltra(del.filter);
}

interface LastnostiDela {
  podjetje: string;
  parametri: [string, string][];
  /** Znesek dela za povzetek („N knjižb, skupaj X"). */
  znesek: string;
  /** Širine stolpcev so skupne vsem delom, da so leti poravnani. */
  sirine: SirineStolpcev;
  naSirine: (sirine: SirineStolpcev) => void;
  /** Skrbnik lahko pri vsaki knjižbi odpre izvirno vrstico iz datoteke uvoza (5.6). */
  naIzvirnoVrstico?: (knjizba: KnjizbaZaPrikaz) => void;
}

/**
 * Knjižbe enega dela razčlenitve.
 *
 * Vsak del bere svoje knjižbe, zato je svoja komponenta: dve leti sta dve
 * zahtevi in ena ne sme čakati druge.
 */
function DelRazclenitve({
  podjetje,
  parametri,
  znesek: skupaj,
  sirine,
  naSirine,
  naIzvirnoVrstico,
}: LastnostiDela): JSX.Element {
  const b = useBesedila();
  const jezik = useJezik();
  const [knjizbe, setKnjizbe] = useState<KnjizbaZaPrikaz[] | null>(null);
  const [steviloKnjizb, setSteviloKnjizb] = useState<number | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [nalaga, setNalaga] = useState(false);

  const kljucParametrov = new URLSearchParams(parametri).toString();

  useEffect(() => {
    let prekinjeno = false;
    setNalaga(true);
    setNapaka(null);
    setKnjizbe(null);
    api
      .razclenitev(podjetje, parametri, jezik)
      .then((r) => {
        if (prekinjeno) return;
        setKnjizbe(r.knjizbe ?? []);
        setSteviloKnjizb(r.steviloKnjizb);
      })
      .catch((e: unknown) => {
        if (!prekinjeno) setNapaka(e instanceof Error ? e.message : b.knjizbeNapaka);
      })
      .finally(() => {
        if (!prekinjeno) setNalaga(false);
      });
    return () => {
      prekinjeno = true;
    };
  }, [podjetje, kljucParametrov, jezik]);

  if (nalaga) return <p>{b.beremKnjizbe}</p>;
  if (napaka !== null) return <p className="napaka">{napaka}</p>;
  if (knjizbe === null) return <></>;

  return (
    <>
      <div className="vrstica-povzetka">
        <p className="povzetek">
          {steviloKnjizb === 0 ? b.niKnjizb : b.povzetek(steviloKnjizb ?? 0, znesekZValuto(skupaj))}
          {steviloKnjizb !== null && knjizbe.length < steviloKnjizb
            ? b.prikazanihPrvih(knjizbe.length)
            : ''}
        </p>
        {steviloKnjizb !== null && steviloKnjizb > 0 && (
          <p>
            <a
              className="povezava"
              href={`/api/podjetja/${podjetje}/razclenitev/izvoz?${new URLSearchParams([
                ...parametri,
                ['jezik', jezik],
              ]).toString()}`}
            >
              {b.prenesiKnjizbe}
            </a>
          </p>
        )}
        {sirine !== null && (
          <p>
            <button
              type="button"
              className="povezava"
              onClick={() => {
                naSirine(null);
                shraniSirine(null);
              }}
            >
              {b.ponastaviSirine}
            </button>
          </p>
        )}
      </div>
      {knjizbe.length > 0 && (
        /* Tabela ima svoj okvir z drsniki: pri nekaj tisoc vrsticah bi bil
           vodoravni drsnik panela sele na dnu seznama in do desnih
           stolpcev ne bi bilo mogoce priti. */
        <div className="okvir-knjizb">
          <TabelaKnjizb
            knjizbe={knjizbe}
            sirine={sirine}
            naSirine={naSirine}
            {...(naIzvirnoVrstico === undefined ? {} : { naIzvirnoVrstico })}
          />
        </div>
      )}
    </>
  );
}

/**
 * Razčlenitev celice.
 *
 * Vedno pokaže knjižbe, iz katerih je številka sestavljena — tudi pri
 * nadrejeni skupini in pri skupni vrstici. Ravni so že v tabeli poročila,
 * zato jih panel ne podvaja: klik na skupino ne sme zahtevati, da se
 * uporabnik najprej prebije skozi konte (docs/ARHITEKTURA.md, 6.2).
 */
export function PanelRazclenitve({ podjetje, celica, naZapri }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const uporabnik = useUporabnik();
  const vrstica = celica.vrstica;
  const deli = deliCelice(celica);

  /**
   * Celica izkaza poslovnega izida je brez zaključnih temeljnic in tako tudi
   * njena razčlenitev - kar ni v številki, ni v razčlenitvi. Skrbnik jih za
   * preverjanje lahko pokaže; stranka tega stikala ne vidi (5.9).
   */
  const imaIzlocene =
    deli.length === 0
      ? vrstica.filter.brezZapiranja === true
      : deli.some((d) => d.filter.brezZapiranja === true);
  const [zIzlocenimi, setZIzlocenimi] = useState(false);
  const [sirine, setSirine] = useState<SirineStolpcev>(preberiSirine);
  const dodatni: [string, string][] = zIzlocenimi ? [['zIzlocenimi', 'true']] : [];

  // Izvirna vrstica knjižbe (samo skrbnik) je plast nad panelom; odprta je
  // največ ena, zato njeno stanje drži panel, ne posamezni del.
  const [izvirna, setIzvirna] = useState<KnjizbaZaPrikaz | null>(null);
  const izvirneNaVoljo = uporabnik.jeAdmin;

  // Esc zapre razčlenitev tako kot ×. Dokler je odprta izvirna vrstica, Esc
  // zapre samo njo (njen poslušalec), panel pa ostane: oba poslušalca sta na
  // dokumentu in drug drugega ne ustavita, zato panel ob odprtem oknu Esc
  // prezre.
  useEffect(() => {
    const naTipko = (d: KeyboardEvent): void => {
      if (d.key === 'Escape' && izvirna === null) naZapri();
    };
    document.addEventListener('keydown', naTipko);
    return () => {
      document.removeEventListener('keydown', naTipko);
    };
  }, [naZapri, izvirna]);

  return (
    // Plast lezi cez desni del porocila in ga ne skrci: porocilo ostane v isti
    // sirini in na istem mestu, levo pa se vidi naziv odprte vrstice
    // (docs/ARHITEKTURA.md, 6.2).
    <div className="plast-razclenitve">
      {/* Pri vec kot dveh delih (npr. denarni tok iz poslovanja ima deset
        postavk) si deli visine panela ne morejo razdeliti - panel drsi v
        celoti, vsak del pa ima svoj okvir knjizb. */}
      <aside className={deli.length > 2 ? 'panel vec-delov' : 'panel'} aria-label={b.razclenitev}>
        <header>
          <div>
            <p className="oznaka-razclenitve">{b.razclenitev}</p>
            <p className="pot-razclenitve">
              {[
                ...(celica.pot ?? []),
                vrstica.sifra === null ? vrstica.naziv : `${vrstica.sifra} ${vrstica.naziv}`,
                celica.naslovObdobja,
              ].join(' → ')}
            </p>
          </div>
          <button type="button" className="zapri" onClick={naZapri} aria-label={b.zapriRazclenitev}>
            ×
          </button>
        </header>

        {uporabnik.jeAdmin && imaIzlocene && (
          <label className="stikalo drobno">
            <input
              type="checkbox"
              checked={zIzlocenimi}
              onChange={(e) => {
                setZIzlocenimi(e.target.checked);
              }}
            />
            {b.pokaziIzlocene}
          </label>
        )}

        {deli.length === 0 ? (
          <DelRazclenitve
            podjetje={podjetje}
            parametri={[...parametriDela(celica, null), ...dodatni]}
            znesek={celica.znesek}
            sirine={sirine}
            naSirine={setSirine}
            {...(izvirneNaVoljo ? { naIzvirnoVrstico: setIzvirna } : {})}
          />
        ) : (
          deli.map((del) => (
            <section key={del.naslov} className="del-razclenitve">
              {/* Naslov dela je potreben samo, kadar sta dela dva ali vec —
                sicer bi nad vsako razclenitvijo pisalo isto leto dvakrat. */}
              {deli.length > 1 && <h3>{del.naslov}</h3>}
              <DelRazclenitve
                podjetje={podjetje}
                parametri={[...parametriDela(celica, del), ...dodatni]}
                znesek={del.znesek}
                sirine={sirine}
                naSirine={setSirine}
                {...(izvirneNaVoljo ? { naIzvirnoVrstico: setIzvirna } : {})}
              />
            </section>
          ))
        )}
        {izvirna !== null && (
          <IzvirnaVrstica
            podjetje={podjetje}
            knjizba={izvirna}
            naZapri={() => {
              setIzvirna(null);
            }}
          />
        )}
      </aside>
    </div>
  );
}
